export const LANDING_EXPERIMENT_KEY = 'lp_motion_v1';
export const LANDING_VARIANT_COOKIE = 'harbor_lp';

export type LandingVariant = 'control' | 'motion' | 'preview';

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

function cookieDomain(): string {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname.toLowerCase();
  if (
    host === 'harborcoparent.com' ||
    host === 'www.harborcoparent.com' ||
    host === 'web.harborcoparent.com'
  ) {
    return '; Domain=.harborcoparent.com';
  }
  return '';
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${cookieDomain()}${secure}`;
}

function clearCookie(name: string): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${cookieDomain()}${secure}`;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
}

function stripLpParam(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('lp')) return;
  url.searchParams.delete('lp');
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
}

/**
 * Resolve the sticky landing experiment variant.
 *
 * The FastAPI middleware sets ``harbor_lp`` on HTML document responses before
 * the SPA boots. This helper also supports ``?lp=control|motion|reset`` for
 * local development and QA, and leaves the preview route out of the test.
 */
export function resolveLandingVariant(): LandingVariant {
  if (typeof window === 'undefined') return 'control';

  if (
    window.location.pathname === '/motion' ||
    window.location.pathname.startsWith('/motion/')
  ) {
    return 'preview';
  }

  const params = new URLSearchParams(window.location.search);
  const override = (params.get('lp') || '').trim().toLowerCase();
  if (override === 'control' || override === 'motion') {
    writeCookie(LANDING_VARIANT_COOKIE, override);
    stripLpParam();
    return override;
  }
  if (override === 'reset') {
    clearCookie(LANDING_VARIANT_COOKIE);
    stripLpParam();
    return 'control';
  }

  const cookieValue = (readCookie(LANDING_VARIANT_COOKIE) || '')
    .trim()
    .toLowerCase();
  return cookieValue === 'motion' ? 'motion' : 'control';
}

export function landingExperimentProps(
  variant: LandingVariant
): Record<string, string | boolean> {
  if (variant === 'preview') {
    return {
      experiment: LANDING_EXPERIMENT_KEY,
      landing_variant: 'preview',
      experiment_preview: true,
    };
  }
  return {
    experiment: LANDING_EXPERIMENT_KEY,
    landing_variant: variant,
  };
}

/** The variant to attach to attribution rows (never the preview route). */
export function attributionLandingVariant(): 'control' | 'motion' | undefined {
  const variant = resolveLandingVariant();
  if (variant === 'control' || variant === 'motion') return variant;
  return undefined;
}

export { readCookie, writeCookie };
